import { z } from 'zod';
import { jsonText } from '@/lib/json';

type T = (key: string, opts?: Record<string, unknown>) => string;

export const SUPPORTED_LOCALES = ['de', 'fr', 'it', 'en'] as const;

/** "de, fr" → ['de','fr'] (trimmed, lowercased, de-duplicated). */
export function parseLocales(text: string): string[] {
  return [...new Set(text.split(/[,\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];
}

const localesField = (t: T) =>
  z
    .string()
    .refine((v) => parseLocales(v).length > 0, t('admin.validation.localesRequired'))
    .refine((v) => parseLocales(v).every((l) => /^[a-z]{2}$/.test(l)), t('admin.validation.localesFormat'));

const timezoneField = (t: T) =>
  z.string().refine((tz) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: tz });
      return tz.length > 0;
    } catch {
      return false;
    }
  }, t('admin.validation.timezone'));

export function createTenantSchema(t: T) {
  return z.object({
    name: z.string().trim().min(2, t('admin.validation.required')),
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/, t('admin.validation.slug')),
    timezone: timezoneField(t),
    locales: localesField(t),
    residency: z.enum(['any', 'eu']),
  });
}
export type CreateTenantForm = z.infer<ReturnType<typeof createTenantSchema>>;

export function generalSchema(t: T) {
  return z.object({
    name: z.string().trim().min(2, t('admin.validation.required')),
    timezone: timezoneField(t),
    locales: localesField(t),
    residency: z.enum(['any', 'eu']),
    status: z.enum(['active', 'suspended', 'archived']),
  });
}
export type GeneralForm = z.infer<ReturnType<typeof generalSchema>>;

/** The backend verifies and stores the pair together, so a new secret is always required (no partial update). */
export function jdeCredentialsSchema(t: T) {
  return z.object({
    clientId: z.string().trim().min(1, t('admin.validation.required')),
    clientSecret: z.string().trim().min(1, t('admin.validation.secretRequired')),
  });
}
export type JdeCredentialsForm = z.infer<ReturnType<typeof jdeCredentialsSchema>>;

/** private_key_jwt (ADR 0021): the key is generated server-side; the admin enters the client id and kid from jde-napi. */
export function jdeKeyCredentialsSchema(t: T) {
  return z.object({
    clientId: z.string().trim().min(1, t('admin.validation.required')),
    keyId: z.string().trim().min(1, t('admin.validation.required')),
  });
}
export type JdeKeyCredentialsForm = z.infer<ReturnType<typeof jdeKeyCredentialsSchema>>;

/** Manual consent change: an opt-in needs an attestation; an opt-out does not. */
export function consentSchema(t: T) {
  return z
    .object({
      number: z.string().trim().min(6, t('admin.validation.required')),
      status: z.enum(['optedIn', 'optedOut']),
      attestation: z.string().trim(),
      contactRef: z.string().trim(),
    })
    .refine((v) => v.status === 'optedOut' || v.attestation.length > 0, {
      path: ['attestation'],
      message: t('pilot.consents.attestationRequired'),
    });
}
export type ConsentForm = z.infer<ReturnType<typeof consentSchema>>;

const e164 = /^\+[1-9]\d{6,14}$/;

export function whatsappSchema(t: T, tokenConfigured: boolean) {
  return z.object({
    phoneNumberId: z.string().regex(/^\d{5,20}$/, t('admin.validation.numericId')),
    wabaId: z.string().regex(/^\d{5,20}$/, t('admin.validation.numericId')),
    displayPhone: z.string().regex(e164, t('admin.validation.e164')),
    systemUserToken: tokenConfigured ? z.string() : z.string().trim().min(10, t('admin.validation.required')),
    appId: z.string().optional(),
    businessId: z.string().optional(),
    templateNamespace: z.string().optional(),
  });
}
export type WhatsappForm = z.infer<ReturnType<typeof whatsappSchema>>;

/** Alphanumeric SMS sender id: 1–11 letters/digits/spaces with at least one letter (05 §3). */
export const SENDER_ID = /^(?=.*[A-Za-z])[A-Za-z0-9 ]{1,11}$/;

/**
 * Credentials are write-only: with stored credentials all secret fields may stay empty (keep); otherwise, or when any
 * secret field is filled, the provider's complete set is required.
 */
function requireAll(ctx: z.RefinementCtx, configured: boolean, fields: Record<string, string>, message: string) {
  const filled = Object.values(fields).some((v) => v.trim() !== '');
  if (configured && !filled) return;
  for (const [path, value] of Object.entries(fields)) {
    if (value.trim() === '') ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  }
}

export function smsSchema(t: T, credentialsConfigured: boolean) {
  return z
    .object({
      provider: z.literal('twilio'),
      fromNumber: z.string().regex(e164, t('admin.validation.e164')),
      senderId: z.string().trim().refine((v) => v === '' || SENDER_ID.test(v), t('admin.validation.senderId')),
      accountSid: z.string(),
      authToken: z.string(),
    })
    .superRefine((v, ctx) =>
      requireAll(ctx, credentialsConfigured, { accountSid: v.accountSid, authToken: v.authToken }, t('admin.validation.required')),
    );
}
export type SmsForm = z.infer<ReturnType<typeof smsSchema>>;

const httpsUrl = /^https:\/\/\S+$/i;
const hexColor = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function emailSchema(t: T, credentialsConfigured: boolean) {
  return z
    .object({
      provider: z.enum(['sendgrid', 'ses']),
      fromAddress: z.string().email(t('admin.validation.email')),
      fromName: z.string().trim().min(1, t('admin.validation.required')).max(100),
      replyToDomain: z.string().regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i, t('admin.validation.domain')),
      apiKey: z.string(),
      accessKeyId: z.string(),
      secretAccessKey: z.string(),
      region: z.string(),
      logoUrl: z.string().trim().refine((v) => v === '' || httpsUrl.test(v), t('admin.validation.httpsUrl')),
      primaryColor: z.string().trim().refine((v) => v === '' || hexColor.test(v), t('admin.validation.hexColor')),
      footerText: z.string().max(1000),
      imprint: z.string().max(1000),
    })
    .superRefine((v, ctx) => {
      const required = t('admin.validation.required');
      if (v.provider === 'sendgrid') requireAll(ctx, credentialsConfigured, { apiKey: v.apiKey }, required);
      else requireAll(ctx, credentialsConfigured, { accessKeyId: v.accessKeyId, secretAccessKey: v.secretAccessKey, region: v.region }, required);
    });
}
export type EmailForm = z.infer<ReturnType<typeof emailSchema>>;

export function testSendSchema(t: T, kind: string) {
  return z.object({
    to: kind === 'email' ? z.string().email(t('admin.validation.email')) : z.string().regex(e164, t('admin.validation.e164')),
    text: z.string().trim().min(1, t('admin.validation.required')).max(1024),
  });
}
export type TestSendForm = z.infer<ReturnType<typeof testSendSchema>>;

export function userSchema(t: T) {
  return z.object({
    email: z.string().email(t('admin.validation.email')),
    displayName: z.string().trim().min(1, t('admin.validation.required')),
    password: z.string().min(12, t('admin.validation.passwordLength')),
    roles: z.array(z.string()).min(1, t('admin.validation.roleRequired')),
  });
}
export type UserForm = z.infer<ReturnType<typeof userSchema>>;

const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A phone number as typed (digits, spaces, + ( ) . / -) — the API normalises it to E.164 (CH default). */
const phoneLike = /^\+?[\d\s()./-]{6,24}$/;

/** Splits the test-recipients textarea into its non-empty lines. */
export function recipientLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export function isTestRecipient(line: string): boolean {
  return line.includes('@') ? z.string().email().safeParse(line).success : phoneLike.test(line) && /\d{6}/.test(line.replace(/\D/g, ''));
}

export function settingsSchema(t: T) {
  return z.object({
    quietStart: z.string().regex(hhmm, t('admin.validation.time')),
    quietEnd: z.string().regex(hhmm, t('admin.validation.time')),
    quietTimezone: timezoneField(t),
    weekendsSilent: z.boolean(),
    notificationEmails: z
      .string()
      .refine((v) => v.split(/[,\s]+/).filter(Boolean).every((e) => z.string().email().safeParse(e).success), t('admin.validation.emailList')),
    webhookUrl: z.union([z.literal(''), z.string().url(t('admin.validation.url'))]),
    digest: z.boolean(),
    reviewerUserIds: z.array(z.string()),
    fallbackText: z.string().max(1024),
    i18nOverrides: jsonText('object', t('admin.validation.jsonObject')),
    testRecipients: z.string().refine((v) => recipientLines(v).every(isTestRecipient), t('admin.validation.testRecipients')),
  });
}
export type SettingsForm = z.infer<ReturnType<typeof settingsSchema>>;

export function templateSchema(t: T) {
  return z.object({
    name: z.string().regex(/^[a-z0-9_]{1,512}$/, t('admin.validation.templateName')),
    language: z.enum(SUPPORTED_LOCALES),
    bodyText: z.string().trim().min(1, t('admin.validation.required')).max(1024),
    examples: z.string(),
    quickReplies: z.string(),
    footer: z.string().max(60),
  });
}
export type TemplateForm = z.infer<ReturnType<typeof templateSchema>>;

/** Number of positional params `{{1}}..{{n}}` in a template body; they must be contiguous from 1. */
export function templateParamCount(body: string): { count: number; contiguous: boolean } {
  const nums = [...body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  const unique = [...new Set(nums)].sort((a, b) => a - b);
  return { count: unique.length, contiguous: unique.every((n, i) => n === i + 1) };
}
